// 遠端協助的畫面形狀（SPEC #kinio-kms-remote、#kinio-kms-api；kmgr fc3f36e internal/api/remote.go、internal/remote View）。
// kmgr 的原始形狀跟畫面形狀幾乎一樣（只多了畫面自己補的名稱），所以不另寫 wire；欄位以 kmgr 的 remote.View 為準。

/** 權限：看（必有）/ 控制 / 剪貼簿 / 檔案傳輸（component/protocol NormalizeKmgrRemotePermissions 的順序）。 */
export type RemotePerm = 'view' | 'control' | 'clipboard' | 'file_transfer';
export const REMOTE_PERMS: RemotePerm[] = ['view', 'control', 'clipboard', 'file_transfer'];

/** requested → awaiting_consent → active ⇄ upgrade_pending → ended（SPEC 狀態圖）。 */
export type RemoteState = 'requested' | 'awaiting_consent' | 'active' | 'upgrade_pending' | 'ended';

/** 結束原因（SPEC #kinio-kms-remote 結束原因；M4-01 加 rejected / expired / connect_failed / connection_lost）。 */
export type RemoteEndReason = 'denied' | 'reported' | 'timeout' | 'operator_end' | 'user_end' | 'revoked' | 'time_limit' | 'kill_switch' | 'rejected' | 'expired' | 'connect_failed' | 'connection_lost';

/** 一個遠端 session（kmgr remote.View）。 */
export interface RemoteSession {
  session_id: string;
  home_id: string;
  device_ref: string;
  mode: 'attended' | 'unattended';
  /** 發起人 user_id */
  operator: string;
  via_delegation_id?: string;
  permissions: RemotePerm[];
  rev: number;
  state: RemoteState;
  ticket_hash: string;
  ends_at: string;
  created_at: string;
  started_at?: string;
  active_at?: string;
  ended_at?: string;
  end_reason?: RemoteEndReason | string;
  /** 2 = UDP 中繼、3 = TCP、4 = TLS 443（x_relay_kind）；3 / 4 畫面寫「連線較慢」 */
  relay_kind?: number;
  reason?: string;
  assist_request_id?: string;
  note?: string;
  external_ticket?: string;
  recording: boolean;
  /** 螢幕鎖定、Agent 留著票等解鎖到這個時間（還在 requested；解鎖後自己跳允許視窗；M4-06b） */
  wait_unlock_until?: string;
  // ---- 跨主機紀錄清單（GET /v1/me/kmgr/remote-sessions）才有；名稱由 names 或畫面補 ----
  org_id?: string;
  operator_name?: string;
  host_name?: string;
  home_name?: string;
  org_name?: string;
  duration_seconds?: number;
}

/** session 歷程（GET …/{session} 的 events；append-only）。 */
export interface RemoteEvent {
  /** created / started / waiting_unlock / unlocked / console / launched / launch_issued / consent / permissions_changed / upgraded / tunnel / file_transferred / ended / note */
  kind: string;
  rev?: number;
  detail: Record<string, unknown>;
  at: string;
}

export interface RemoteSessionDetail {
  session: RemoteSession;
  events: RemoteEvent[];
}

/** 建立：POST …/things/{device}/remote-sessions。 */
export interface RemoteCreateInput {
  mode: 'attended';
  permissions: RemotePerm[];
  reason?: string;
  assist_request_id?: string;
}

export interface RemoteCreated {
  session_id: string;
  ticket_hash: string;
  expires_at: string;
}

/** 開始：…/start {receipt} → 叫起 kmgrctl 的網址。 */
export interface RemoteStarted {
  launch_url: string;
  state: RemoteState;
}

/** 協助請求（GET /v1/orgs/{org}/kmgr/assist-requests；kmgr 只回開著的）。 */
export interface AssistRequest {
  request_id: string;
  org_id: string;
  home_id: string;
  device_ref: string;
  /** 6 位數求助碼 */
  code: string;
  /** 員工打的一句問題（純文字、≤ 200 字） */
  note?: string;
  state: 'open' | 'taken' | 'expired' | 'canceled';
  expires_at: string;
  created_at: string;
  /** 接手的人（kmgr 現在不回 taken 的請求；有就畫「〈人〉已接手」） */
  taken_by_name?: string;
}

/** 紀錄清單的查詢（期間 = 開始時間落在 [from, to)）。 */
export interface RecordsQuery {
  scope: string;
  from: string;
  to: string;
}

export interface RecordsResult {
  sessions: RemoteSession[];
  /** full = 範圍內每個家戶都看得到全部；partial = 有些家戶只看得到自己發起的；own = 全部只看得到自己發起的（kmgr M4-06b） */
  access: 'full' | 'partial' | 'own';
}
